import React from 'react';
import { Box, Card, CardHeader, CardContent, Typography, CircularProgress, Alert, Tooltip } from '@mui/material';
import { PieChart } from '@mui/x-charts';
import { translate as t } from 'hooks/useTranslations';
import { neutral } from '_lex/_foundations/phxColors';
import { ComplianceOverviewSummary } from '../../_types/risk_pool';
import { compliancePerformanceColors } from '..';

interface ComplianceOverviewProps {
  data?: ComplianceOverviewSummary;
  isLoading: boolean;
  isFetching: boolean;
  isError?: boolean;
  error?: Error | null;
  updateParams: (params: { compliance: string; }) => void;
}

interface PieDataItem {
  id: number;
  value: number;
  label: string;
  color: string;
  performance: string;
  tooltip: string;
}

const ComplianceOverview: React.FC<ComplianceOverviewProps> = ({ data, isLoading, isFetching, isError, error, updateParams }) => {
  const meetsAll = data?.meetsAll ?? 0;
  const meetsMajority = data?.meetsMajority ?? 0;
  const meetsSome = data?.meetsSome ?? 0;
  const meetsNone = data?.meetsNone ?? 0;
  const total = data?.total ?? 0;

  const pieData: PieDataItem[] = [
    {
      id: 0,
      value: meetsAll,
      label: t('dashboard.riskPool.complianceOverview.meetsAll'),
      color: compliancePerformanceColors.meetsAll,
      performance: 'meetsAll',
      tooltip: t('dashboard.riskPool.complianceOverview.meetsAllTooltip'),
    },
    {
      id: 1,
      value: meetsMajority,
      label: t('dashboard.riskPool.complianceOverview.meetsMajority'),
      color: compliancePerformanceColors.meetsMajority,
      performance: 'meetsMajority',
      tooltip: t('dashboard.riskPool.complianceOverview.meetsMajorityTooltip'),
    },
    {
      id: 2,
      value: meetsSome,
      label: t('dashboard.riskPool.complianceOverview.meetsSome'),
      color: compliancePerformanceColors.meetsSome,
      performance: 'meetsSome',
      tooltip: t('dashboard.riskPool.complianceOverview.meetsSomeTooltip'),
    },
    {
      id: 3,
      value: meetsNone,
      label: t('dashboard.riskPool.complianceOverview.meetsNone'),
      color: compliancePerformanceColors.meetsNone,
      performance: 'meetsNone',
      tooltip: t('dashboard.riskPool.complianceOverview.meetsNoneTooltip'),
    },
  ];

  const percentages = pieData.map((item) => ({
    ...item,
    percentage: total > 0 ? Math.round((item.value / total) * 100) : 0,
  }));

  return (
    <Card className='lex-chart-card'>
      <CardHeader title={t('dashboard.riskPool.complianceOverview.title')} />
      <CardContent sx={{ minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {(isLoading || isFetching) && (
          <CircularProgress />
        )}
        {isError && (
          <Alert severity="error">
            {error?.message || t('dashboard.riskPool.complianceOverview.error')}
          </Alert>
        )}
        {!isLoading && !isFetching && !isError && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '160px 1fr',
                gap: 10,
                minHeight: 0,
                width: 'fit-content',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: 160, height: 160 }}>
                <PieChart
                  series={[
                    {
                      data: pieData,
                      innerRadius: 50,
                      outerRadius: 80,
                      paddingAngle: 0,
                      cornerRadius: 0,
                      startAngle: 0,
                      endAngle: 360,
                    },
                  ]}
                  slotProps={{
                    legend: { hidden: true },
                  }}
                  onItemClick={(event, item) => {
                    const dataItem = pieData[item.dataIndex];
                    updateParams({ compliance: dataItem.performance });
                  }}
                  width={160}
                  height={160}
                  colors={Object.values(compliancePerformanceColors)}
                  margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                />
              </Box>
              <Box
                className='legend'
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 7,
                  minHeight: 0,
                  alignItems: 'center',
                }}
              >
                {percentages.map((item) => (
                  <Tooltip key={item.id} title={item.tooltip} arrow placement="top">
                    <Box
                      className='legend-item'
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '16px 1fr',
                        columnGap: 3,
                        rowGap: 1,
                        minHeight: 0,
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                      onClick={() => updateParams({ compliance: item.performance })}
                    >
                      <Box sx={{ display: 'flex', borderRadius: 9999, width: 8, height: 8, backgroundColor: item.color }} />
                      <Typography
                        variant='bodyXs'
                        sx={{ color: neutral[700] }}
                      >
                        {item.label}
                      </Typography>
                      <Box />
                      <Typography variant='subtitleMd'>{item.percentage}%</Typography>
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card >
  );
};

export default ComplianceOverview;
